import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import type { UserRole } from "bancojs";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";
import { ADMIN_SECTIONS, MY_SECTIONS, SYSTEM_SECTIONS, firstAccessible } from "./sections";

import Welcome from "@/views/Welcome.vue";
import Gateway from "@/views/Gateway.vue";
import Layout from "@/views/Layout.vue";
import AdminUsersIndex from "@/views/admin/users/Index.vue";
import AdminUsersEdit from "@/views/admin/users/Edit.vue";
import AdminClientsIndex from "@/views/admin/clients/Index.vue";
import AdminClientsEdit from "@/views/admin/clients/Edit.vue";
import AdminMappersIndex from "@/views/admin/mappers/Index.vue";
import AdminMappersEdit from "@/views/admin/mappers/Edit.vue";
import AdminKeysIndex from "@/views/admin/keys/Index.vue";
import AdminKeysEdit from "@/views/admin/keys/Edit.vue";
import AdminKeysCsrIndex from "@/views/admin/keys/csr/Index.vue";
import AdminKeysCsrCreate from "@/views/admin/keys/csr/Create.vue";
import AdminCertificatesIndex from "@/views/admin/certificates/Index.vue";
import AdminCertificatesEdit from "@/views/admin/certificates/Edit.vue";
import AdminCertificatesCreate from "@/views/admin/certificates/Create.vue";
import SystemKeysIndex from "@/views/system/keys/Index.vue";
import SystemKeysCreate from "@/views/system/keys/Edit.vue";
import MyProfile from "@/views/my/Profile.vue";
import MyCertificatesIndex from "@/views/my/certificates/Index.vue";
import MyCertificatesEdit from "@/views/my/certificates/Edit.vue";

declare module "vue-router" {
  interface RouteMeta {
    /** Accesible sin sesión. */
    public?: boolean;
    /** Alcanza con CUALQUIERA de estos roles. Vacío u omitido = sólo autenticado. */
    roles?: readonly UserRole[];
    /** Qué drawer mostrar: "/my" | "/admin" | "/system". */
    prefix?: string;
  }
}

const myRoutes: RouteRecordRaw[] = [
  { path: "profile", component: MyProfile },
  { path: "certificates", component: MyCertificatesIndex },
  { path: "certificates/:fingerprint/edit", component: MyCertificatesEdit },
  { path: "", redirect: "/my/profile" },
];

const adminRoutes: RouteRecordRaw[] = [
  { path: "users", component: AdminUsersIndex, meta: { roles: ["ADMIN", "ADMIN_USERS"] } },
  { path: "users/new", component: AdminUsersEdit, meta: { roles: ["ADMIN", "ADMIN_USERS"] } },
  { path: "users/:id/edit", component: AdminUsersEdit, meta: { roles: ["ADMIN", "ADMIN_USERS"] } },

  { path: "clients", component: AdminClientsIndex, meta: { roles: ["ADMIN", "ADMIN_CLIENTS"] } },
  { path: "clients/new", component: AdminClientsEdit, meta: { roles: ["ADMIN", "ADMIN_CLIENTS"] } },
  { path: "clients/:id/edit", component: AdminClientsEdit, meta: { roles: ["ADMIN", "ADMIN_CLIENTS"] } },

  { path: "keys", component: AdminKeysIndex, meta: { roles: ["ADMIN", "ADMIN_KEYS"] } },
  { path: "keys/new", component: AdminKeysEdit, meta: { roles: ["ADMIN", "ADMIN_KEYS"] } },
  { path: "keys/:id/edit", component: AdminKeysEdit, meta: { roles: ["ADMIN", "ADMIN_KEYS"] } },
  { path: "keys/:key_id/csr", component: AdminKeysCsrIndex, meta: { roles: ["ADMIN", "ADMIN_KEYS"] } },
  { path: "keys/:key_id/csr/new", component: AdminKeysCsrCreate, meta: { roles: ["ADMIN", "ADMIN_KEYS"] } },

  { path: "certificates", component: AdminCertificatesIndex, meta: { roles: ["ADMIN", "ADMIN_CERTIFICATES"] } },
  { path: "certificates/new", component: AdminCertificatesCreate, meta: { roles: ["ADMIN", "ADMIN_CERTIFICATES"] } },
  {
    path: "certificates/:fingerprint/edit",
    component: AdminCertificatesEdit,
    meta: { roles: ["ADMIN", "ADMIN_CERTIFICATES"] },
  },

  { path: "mappers", component: AdminMappersIndex, meta: { roles: ["ADMIN", "ADMIN_CERTIFICATES_MAPPERS"] } },
  { path: "mappers/new", component: AdminMappersEdit, meta: { roles: ["ADMIN", "ADMIN_CERTIFICATES_MAPPERS"] } },
  {
    path: "mappers/:id/edit",
    component: AdminMappersEdit,
    meta: { roles: ["ADMIN", "ADMIN_CERTIFICATES_MAPPERS"] },
  },

  // La entrada del portal NO se resuelve acá con un `redirect`: los redirects de
  // registro se evalúan al hacer match de la ruta, o sea ANTES del beforeEach
  // que trae el perfil. Con `profile` todavía en null, canAccess daba false
  // para todo y un ADMIN_KEYS que entraba por URL directa a /admin terminaba en
  // /my/profile. Lo resuelve el guard, ya con el perfil cargado.
];

const systemRoutes: RouteRecordRaw[] = [
  { path: "keys", component: SystemKeysIndex, meta: { roles: ["ADMIN", "ADMIN_SYSTEM"] } },
  { path: "keys/new", component: SystemKeysCreate, meta: { roles: ["ADMIN", "ADMIN_SYSTEM"] } },
  // Sin ruta keys/:id/edit: no existe GET /system/keys/{id} con el que cargar un
  // formulario. Lo único editable —la marca de default— es una acción de fila.
  // La entrada del portal la resuelve el guard; ver la nota en adminRoutes.
];

/**
 * El redirect anterior de `/admin` iba fijo a `/admin/users`, así que un usuario
 * con sólo ADMIN_KEYS aterrizaba en un 403 en vez de en la sección que sí puede
 * ver.
 */
function resolveSectionEntry(group: "admin" | "system"): string {
  const auth = useAuthStore();
  const sections = group === "admin" ? ADMIN_SECTIONS : SYSTEM_SECTIONS;
  return (
    firstAccessible(sections, (roles) => auth.canAccess(roles)) ??
    firstAccessible(MY_SECTIONS, () => true) ??
    "/my"
  );
}

const routes: RouteRecordRaw[] = [
  { path: "/", name: "Welcome", component: Welcome, meta: { public: true } },
  { path: "/gateway", name: "Gateway", component: Gateway },
  // `meta` se hereda de la ruta padre en Vue Router 4+, así que `prefix` se
  // declara UNA vez acá en vez de repetirse en cada hijo, como antes.
  { path: "/my", component: Layout, children: myRoutes, meta: { prefix: "/my" } },
  { path: "/admin", component: Layout, children: adminRoutes, meta: { prefix: "/admin" } },
  { path: "/system", component: Layout, children: systemRoutes, meta: { prefix: "/system" } },
  // Antes no había catch-all: una URL desconocida no renderizaba nada.
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export function createAppRouter() {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
  });

  // En Vue Router 4+ los guards DEVUELVEN el destino en vez de llamar a next().
  // Eso además elimina estructuralmente el bug del guard anterior de /gateway,
  // que llamaba a next() dos veces cuando `redirect_to` contenía "/admin".
  router.beforeEach(async (to) => {
    const auth = useAuthStore();

    if (to.meta.public) return true;

    // No tener token localmente es motivo para intentar reautenticar en
    // silencio, no para expulsar al usuario: la sesión vive en el IDP y el
    // storage local es sólo una caché.
    //
    // Y hace falta de verdad: `automaticSilentRenew` abre un iframe oculto a
    // /silent-renew.html que escribe en el MISMO localStorage. Si se navega
    // mientras esa renovación está en vuelo, oidc-client-ts la considera fallida
    // y borra el usuario guardado. Sin este reintento eso echaba al usuario a la
    // pantalla de login de forma intermitente — medido en 2 de 6 corridas del
    // smoke de roles con renovación activa, y 0 de 3 con ella desactivada.
    let identity = await auth.getIdentity();
    if (!identity) {
      const token = await auth.ensureFreshToken();
      if (token) identity = await auth.getIdentity();
    }
    if (!identity) {
      return { path: "/", query: to.fullPath === "/" ? {} : { redirect_to: to.fullPath } };
    }

    // Los roles viven en banco_users, no en el token: hay que traer el perfil.
    if (!auth.profile) {
      try {
        const { banco } = await import("@/api/client");
        auth.setProfile(await banco.user.profile.get());
      } catch {
        // Token válido pero sin fila en banco_users, o usuario inactivo: la API
        // responde 403 a todo. No hay sesión utilizable.
        await auth.logout({
          post_logout_redirect_uri: `${window.location.origin}/?notify=error`,
        });
        return false;
      }
    }

    // Entrada de un portal: ya con el perfil cargado se puede elegir la primera
    // sección que el usuario realmente pueda ver.
    if (to.path === "/admin" || to.path === "/system") {
      return resolveSectionEntry(to.path === "/admin" ? "admin" : "system");
    }

    if (!auth.canAccess(to.meta.roles)) {
      useNotificationsStore().warning(
        "No tiene permisos para acceder a esa sección.",
        "Acceso denegado"
      );
      // Devolver al portal por el que entró, no siempre al de administración:
      // a un ADMIN_SYSTEM rebotado desde /system no le sirve ir a /admin.
      const group = to.meta.prefix === "/system" ? "system" : "admin";
      return resolveSectionEntry(group);
    }

    return true;
  });

  return router;
}
